import React, { useState } from 'react';
import style from './add-carousel.module.scss';
import { useMutate, useAppAndBranchId } from '../../../../hooks';
import { api } from '../../api';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import { Carousel as TCarousel, CarouselCard, CarouselAction } from '../../types';
import AddIcon from '../../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import { Card } from './Card';
import { Drawer } from '../../../../components';
import { ContentMode } from '../../../../types';
import { validateList, ValidateParams } from '../../../../utils/validation';
import { isValidHttpUrl } from '../../utils';
import { errorMessage } from '../../../../utils/error';

type Carousel = TCarousel['contentValue'];

type Props = {
	isOpen: boolean;
	close: () => void;
	mode?: ContentMode;
	initialValue?: Carousel & { id: string };
	refetchContent: () => void;
	onCreate?: (contentId: string) => void;
	isPreview?: boolean;
};

export function AddCarousel(props: Props) {
	const { ErrorToast, WarningToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const {
		isOpen,
		close,
		initialValue,
		refetchContent,
		onCreate,
		isPreview = false,
	} = props;
	const [state, setState] = useState<Carousel>(
		initialValue ?? {
			title: '',
			cards: [],
		},
	);
	const [mode, setMode] = useState(props.mode ?? 'create');
	const [cardsVisibility, setCardsVisibility] = useState<
		Record<number, boolean>
	>(() => {
		if (initialValue?.cards.length) {
			const visibility: Record<number, boolean> = {};
			initialValue.cards.forEach((_, i) => {
				visibility[i] = false;
			});
			return visibility;
		}
		return {};
	});

	const { mutate: createMutate, loading: createLoading } = useMutate(
		api.createContent,
	);
	const { mutate: updateMutate, loading: updateLoading } = useMutate(
		api.updateContent,
	);

	const [error, setError] = useState<{
		title: string;
		cards: Record<string, string>[];
		minChoice: string;
		actions: Record<string, string>[];
	}>({
		title: '',
		cards: [],
		actions: [],
		minChoice: '',
	});

	function clearErrorList(index: number, name: "cards" | "actions", key: string) {
		setError((prev) => ({
			...prev,
			[name]: prev[name].map((ch, i) =>
				i === index ? { ...ch, [key]: '' } : ch,
			),
		}));
	}

	function clearError(key: keyof typeof error, value: any = '') {
		setError((prev) => ({ ...prev, [key]: value }));
	}

	function addCard() {
		const newCard: CarouselCard = {
			id: state.cards.length,
			title: '',
			subtitle: '',
			description: '',
			url: '',
			actions: {},
		};
		setState((prev) => ({ ...prev, cards: [...prev.cards, newCard] }));
		toggleCard(state.cards.length, {
			...cardsVisibility,
			[state.cards.length]: false,
		});
	}

	function validate() {
		const rulesFn = (prefix: string) => {
			return [
				{
					message: `${prefix} can't be empty`,
					fn: (value: string) => value.trim().length <= 0,
				},
			];
		};

		const cardsValidation = state.cards.map((card) => {
			const validationResult = validateList([
				{ name: 'title', value: card.title, rules: rulesFn('Title') },
				{ name: 'subtitle', value: card.subtitle, rules: rulesFn('Subtitle') },
				{
					name: 'description',
					value: card.description,
					rules: rulesFn('Description'),
				},
				{ name: 'url', value: card.url, rules: [
					{
						message: "Upload an image or provide a valid URL",
						fn: (value: string) => value.trim().length <= 0,
					},
					{
						message: "Upload an image or provide a valid URL",
						fn: (value: string) => !isValidHttpUrl(value),
					},
				] },
			]);
			return validationResult;
		});

		const actionsValidation = state.cards.map((card) => {
			const list: ValidateParams[] = [];

			function actionRule(prefix: string, action: CarouselAction) {
				list.push({
					name: `${prefix}Label`,
					value: action.label ?? "",
					rules: rulesFn('Label'),
				});
				if(action.type === 'openUrl') {
					list.push({
						name: `${prefix}URL`,
						value: action.value ?? "",
						rules: rulesFn('URL'),
					});
				}
			}
			if (card.actions.primary) {
			 actionRule('primary', card.actions.primary)
			}
			if (card.actions.secondary) {
			 actionRule('secondary', card.actions.secondary)
			}
			const validationResult = validateList(list);
			return validationResult;
		});

		const error = {
			cards: cardsValidation,
			actions: actionsValidation,
			title: !state.title.trim().length ? "Title can't be empty" : '',
			minChoice: !state.cards.length
				? 'Atleast one card should be attached'
				: '',
		};

		if (error.minChoice) {
			WarningToast(error.minChoice);
		}

		let hasError = false;

		if (error.title || error.minChoice) {
			hasError = true;
		}

		let cardsError = false
		error.cards.forEach(({ _id, ...rest }) => {
			if (Object.keys(rest).length !== 0) {
				hasError = true;
				cardsError = true
			}
		});

		error.actions.forEach(action => {
			if (Object.keys(action).length !== 0) {
				hasError = true;
				cardsError = true
			}
		});

		if(cardsError) {
		  WarningToast("Cards contains empty or incorrect values")
		}

		setError(error);
		return !hasError;
	}

	async function onSave() {
		if (mode !== 'view' && !validate()) {
			return;
		}
		const payload = {
			branchId,
			contentType: 'cardCarousel',
			contentValue: {
				title: state.title.trim(),
				cards: state.cards,
			},
		};
		try {
			if (mode === 'edit') {
				await updateMutate(appId, initialValue?.id, payload);
			} else if (mode === 'create') {
				const res = await createMutate(appId, payload);
				if (onCreate) {
					onCreate(res.contentId);
				}
			} else {
				setMode('edit');
				return;
			}
			refetchContent();
			close();
		} catch (err) {
			ErrorToast(errorMessage(mode, 'carousel', err.code))
		}
	}

	function onClose() {
		setState({
			title: '',
			cards: [],
		});
		close();
	}

	function toggleCard(index: number, initVisibility?: Record<number, boolean>) {
		const currVisibility = initVisibility ?? cardsVisibility;
		const visibility: Record<number, boolean> = {};
		for (let key in currVisibility) {
			if (parseInt(key) == index) {
				visibility[key] = !(currVisibility[key] ?? false);
			} else {
				visibility[key] = false;
			}
		}
		setCardsVisibility(visibility);
	}

	function updateCard(key: keyof CarouselCard, value: any, index: number) {
		setState((prev) => ({
			...prev,
			cards: prev.cards.map((el, i) =>
				i === index ? { ...el, [key]: value } : el,
			),
		}));
	}

	function removeCard(index: number) {
		setState((prev) => ({
			...prev,
			cards: prev.cards.filter((_, i) => i !== index),
		}));
	}

	// removeErrorForIndex removes the error from the error list
	// for "actions" and "choices" fields
	function removeErrorForIndex(index: number) {
	  clearError('cards', error.cards.filter((_el, i) => i !== index))
	  clearError('actions', error.actions.filter((_el, i) => i !== index))
	}


	return (
		<Drawer
			onSave={onSave}
			onClose={onClose}
			mode={mode}
			isPreview={isPreview}
			visible={isOpen}
			loading={createLoading || updateLoading}
			contentType="cardCarousel"
		>
			<div className={style.addCarousel}>
				<div className={style.main}>
					<div className={style.inputWrapper}>
						<label>Title</label>
						<input
							disabled={mode === 'view'}
							placeholder="Enter title"
							value={state.title}
							onChange={(e) => {
								clearError('title');
								const title = e.currentTarget.value;
								setState((prev) => ({ ...prev, title }));
							}}
						/>
						<span className={style.error}>{error.title}</span>
					</div>
					<div className={style.addCards}>
						{(mode !== 'view' || state.cards.length > 0) && <p>Cards</p>}
						{mode !== 'view' && (
							<>
								<div className={style.line} />
								<button className={style.add} onClick={addCard}>
									<img src={AddIcon} alt="add" />
									<span>Add Card</span>
								</button>
							</>
						)}
					</div>
					<div className={style.cards}>
						{state.cards.map((el, i) => {
							return (
								<Card
									clearError={clearErrorList}
									removeErrorForIndex={removeErrorForIndex}
									error={{ cards: error.cards[i], actions: error.actions[i]}}
									mode={mode}
									key={el.id}
									isOpen={cardsVisibility[i]}
									{...el}
									toggleCard={toggleCard}
									index={i}
									updateCard={updateCard}
									removeCard={removeCard}
									card={el}
									initialValue={initialValue}
								/>
							);
						})}
					</div>
				</div>
			</div>
		</Drawer>
	);
}
